import { deviceDisplayName } from '../transport/deviceName'
import { notifySignedOut } from '../auth/sessionSignal'
/**
 * The servers this account has paired — the outer layer of the + button.
 *
 * SHAPE FIRST, WIRE LATER, and the order is deliberate. The endpoint that lists
 * paired servers is avengents-homepage's to define (asked for in the project channel,
 * 2026-08-04); guessing its path here would couple this app to a contract nobody has
 * agreed to. So this module owns the SHAPE the UI consumes, and `fetchServers` is the
 * single seam where the real endpoint lands.
 *
 * Until then it answers `unwired` — a fourth state, distinct from `unreachable`,
 * because they name different failures: unreachable means the contract exists and the
 * BFF did not answer; unwired means there is nothing to call yet. Collapsing them
 * would send someone to restart a service that is not the problem.
 */

export type PairedServer = {
  server_id: string
  name: string
  /**
   * Three-valued on purpose, mirroring the rest of this codebase: a server that
   * missed a heartbeat is `unknown`, never silently `offline` — the difference is
   * "go fix the tunnel" versus "it is off, expected".
   */
  reachable: 'online' | 'offline' | 'unknown'
  /** The device key's fingerprint, first 8 hex — the account's stable name for the
   *  KEY, so it survives hostname changes and collisions alike. */
  fp: string | null
  /**
   * The build that machine's agent is RUNNING, self-reported on its beat.
   *
   * '' when it has never reported one, which is an OLDER EDGE and not the same
   * fact as "behind": unknown must render as unknown. Paid for on 2026-08-20 —
   * a machine ran 15 commits behind with a dead update channel, and nothing on
   * any screen could have told anyone (owner: put the hash on the machine list
   * and say whether it is out of date).
   */
  version: string
  /**
   * Seconds since the machine was last heard from, or null for NEVER.
   *
   * Renamed off `beat_age_s`: the beat is gone, and a field named after a mechanism
   * that no longer exists reads as documentation of a live thing. Fixing what a name
   * reports while leaving the name wrong is harder to catch than leaving the content
   * broken, because it passes every test (homepage, after deleting `sessions` rather
   * than redefining it).
   *
   * NULL versus a number is the distinction that matters here, and it is the
   * whole point: the owner picked a paired-but-never-connected device out of a list
   * of identical UUIDs and watched ~20 browse commands queue forever on it. "Never
   * connected" invites un-picking; a yellow dot alone invited clicking.
   */
  last_seen_age_s: number | null
  /**
   * The agents running on this machine, or NULL for "the machine has not told us".
   *
   * Empty and unknown are different answers to "what is running here", and the whole
   * point of listing them is the question the owner will ask straight after starting
   * one: where did it go. An empty list says "nothing is running"; null says "we have
   * not heard" — and rendering the second as the first would report a freshly started
   * agent as absent.
   */
  agents: AgentRef[] | null
  agent_count?: number | null
  /**
   * The machine whose shared home this one is a node of (the BFF's `sibling_of`), or null for a
   * machine paired by itself. A node pairs itself under its home (edge nodeEnrol.ts), so a person
   * who paired one login node reads it as part of that machine (shell/machineNodes.ts).
   */
  sibling_of?: string | null
}

export type ServerList =
  | { kind: 'servers'; servers: PairedServer[] }
  /** The contract answered: this account has paired nothing yet. Pairing is the fix. */
  | { kind: 'none' }
  /** The contract exists but did not answer. Starting the BFF / network is the fix. */
  | { kind: 'unreachable'; detail: string }
  /**
   * No contract wired. As of 2026-08-04 this state is UNREACHABLE at runtime —
   * `fetchServers` is wired to /account/devices — but the picker still renders it,
   * and the test that forbids conflating it with `none` still stands. It documents
   * a real period and guards the distinction if the seam is ever unpicked.
   */
  | { kind: 'unwired' }

export async function fetchServers(): Promise<ServerList> {
  let res: Response
  try {
    res = await fetch('/account/devices', { credentials: 'include', cache: 'no-store' })
  } catch (e) {
    return { kind: 'unreachable', detail: String(e) }
  }
  if (res.status === 401) {
    notifySignedOut()
    // Not signed in. The gate in front of the app should make this unreachable, but
    // a session can expire while the sheet is open — name it rather than melt it
    // into "could not fetch".
    return { kind: 'unreachable', detail: 'Signed out — sign in again.' }
  }
  if (!res.ok) return { kind: 'unreachable', detail: `/account/devices answered ${res.status}` }

  // The server's own clock, before anything is derived from its timestamps.
  noteServerClock(res.headers?.get?.('date') ?? null)

  let body: { devices?: unknown }
  try {
    body = (await res.json()) as { devices?: unknown }
  } catch {
    return { kind: 'unreachable', detail: '/account/devices did not return JSON' }
  }
  if (!Array.isArray(body.devices)) {
    return { kind: 'unreachable', detail: '/account/devices returned no devices array' }
  }
  if (body.devices.length === 0) return { kind: 'none' }

  return toServerList(body.devices as unknown[])
}

/* ── creating an agent on a device ──────────────────────────────────────────
   Not synchronous, and the shape says why: the edge is pure-outbound, so the cloud
   can only queue the command for the machine's next beat. What is refused HERE is
   only what the BFF can judge alone (bad cli, bad session name, not your device) —
   refused while someone is watching, which beats failing thirty seconds later in a
   place nobody reads. */

export type CreateRejection = { code: string; detail: string }

export async function createAgent(req: {
  device_id: string
  session: string
  cli: string | null
  cwd: string | null
}): Promise<
  | { ok: true; command_id: string; collect_within_s: number }
  | { ok: false; rejection: CreateRejection }
> {
  let res: Response
  try {
    res = await fetch('/account/agents/create', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    })
  } catch (e) {
    return { ok: false, rejection: { code: 'network', detail: String(e) } }
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (res.status === 202) {
    return {
      ok: true,
      command_id: String(body.command_id ?? ''),
      collect_within_s: typeof body.collect_within_s === 'number' ? body.collect_within_s : 30,
    }
  }
  // 400 unknown_cli / bad_session_name, 404 unknown_device — the named, synchronous
  // rejections. Passed through as themselves so the form can point at the field.
  return {
    ok: false,
    rejection: {
      code: String(body.error ?? res.status),
      // homepage's 409 device_not_collecting carries its advice in `message`
      // (「已經有 N 個在等而它一個都沒收…」) — that sentence IS the product here.
      detail: String(body.message ?? body.detail ?? body.error ?? `create answered ${res.status}`),
    },
  }
}

/**
 * One command's lifecycle. FOUR states, and the pair that must never merge is
 * `queued` and `failed`: "the machine has not seen it yet" and "it saw it and could
 * not" need opposite reactions — wait versus read the error. A long `queued` is not
 * a timeout; it means the device is not beating, and `last_seen` says so.
 */
/**
 * One attached agent, as the roster reports it.
 *
 * `label` is what a person reads; `channel_hash` is what a hold/release frame needs,
 * so both travel together — deriving one from the other is what produced a row of hex
 * the last time this was guessed at.
 *
 * `reported` is separate from `blocked` on purpose, and the reason is the same one
 * that runs through this whole file: without it, `blocked: null` would mean both
 * "answered, and free" and "never answered at all".
 */
export type AgentRef = {
  channel_hash: string
  label: string
  /**
   * The pane's name on that machine, or null when it could not be PROVEN.
   *
   * `label` is a display name and defaults to the session, which is why it looks like
   * an address and is not one — attach accepts a different label, and homepage was
   * sending it to the edge as a session until this was checked. A hold under a name
   * the machine cannot resolve is accepted, protects nobody, and is indistinguishable
   * from one that worked.
   *
   * They now prove it instead of assuming: the channel hash is derived from the
   * session, so recomputing it from the label either reproduces the hash or does not.
   * null means "we cannot address this pane" — never fall back to `label` here, which
   * is the exact substitution the proof exists to prevent.
   */
  session: string | null
  reported: boolean
  blocked: string | null
}

export type CommandState = {
  /**
   * `unknown` is the fifth state, added by the contract after avengents-server
   * named the gap: taken but never reported (the edge died mid-create, or the
   * report was lost). It is NOT failed — create is irreversible and the agent may
   * genuinely exist — and it is not queued either. The only honest instruction is
   * "go look", which is what the Scan button is for.
   */
  state: 'queued' | 'running' | 'done' | 'failed' | 'unknown'
  /**
   * afae062's contract: `cwd` is where the session ACTUALLY started — the pane's own
   * path, read back, not the request echoed. `requested_cwd` appears ONLY when the two
   * differ (tmux creates a session even when `-c` names a directory that does not
   * exist, silently starting elsewhere — measured on their side). Absent when there is
   * nothing to say, so its presence IS the signal.
   */
  result?: { session?: string; cwd?: string; requested_cwd?: string } | null
  error?: string | null
}

export async function getCommand(deviceId: string, commandId: string): Promise<CommandState> {
  const res = await fetch(
    `/account/devices/${encodeURIComponent(deviceId)}/commands/${encodeURIComponent(commandId)}`,
    { credentials: 'include', cache: 'no-store' },
  )
  if (res.status === 401) notifySignedOut()
  if (!res.ok) throw new Error(`command status answered ${res.status}`)
  return (await res.json()) as CommandState
}


/**
 * One mapping, used by both the fetch and the pushed frame.
 *
 * Two copies would drift, and the drift would be invisible: the list drawn on open
 * and the list drawn a second later would disagree about the same machines, with
 * nothing to say which of them was wrong.
 */
/**
 * The server's clock, minus ours — learned, not assumed.
 *
 * Liveness here is `now - last_seen`, and `last_seen` is stamped by the cloud while
 * `now` was the browser's. Two clocks that were never compared, subtracted as if they
 * were one: a guess carrying a resolution, and the resolution is minutes on a phone.
 * The failure leans the wrong way — a browser clock running behind makes a machine that
 * died an hour ago read "online", which is exactly the pick the owner was trapped into
 * once tonight.
 *
 * Every HTTP response carries the server's own clock in its `Date` header, so the
 * offset is observable rather than inferred. Learned on each fetch and applied to
 * pushed frames too, which have no header of their own.
 * (The lesson is avengents-homepage's, from a dedupe that compared two timestamps at
 * one-second resolution and lost every message stored in the connecting second.)
 */
/**
 * null until learned — because 0 already means "the two clocks agree".
 *
 * Found by running avengents-server's audit on my own code an hour after writing it:
 * scan for the values where two different situations converge. A skew of 0 and a skew
 * never learned were the same number, so a missing Date header silently restored the
 * browser-clock subtraction this whole mechanism exists to remove — and restored it
 * invisibly, which is worse than never having fixed it.
 */
let serverSkewS: number | null = null

/** Whether the server's clock has actually been observed. Local: nothing outside
 * this module should be reasoning about freshness at all. */
function serverClockKnown(): boolean {
  return serverSkewS !== null
}

export function noteServerClock(dateHeader: string | null): void {
  if (!dateHeader) return
  const t = Date.parse(dateHeader)
  if (Number.isNaN(t)) return
  serverSkewS = t / 1000 - Date.now() / 1000
}

/** The server's now, as best we can observe it. */
function serverNowS(): number {
  return Date.now() / 1000 + (serverSkewS ?? 0)
}

/**
 * IS THIS MACHINE THERE? One answer, and it is this one.
 *
 * There were two. This file's version checks the pairing state, prefers the BFF's own
 * `live` verdict, and only falls back to subtracting timestamps when the payload is old
 * enough not to carry one — and even then only against the SERVER's clock, never the
 * browser's, which is the guess the block above spent a night removing.
 *
 * ServerPanel had its own:
 *
 *   d.live === true || (d.live == null && Date.now() / 1000 - d.last_seen < 120)
 *
 * Four differences, every one of them able to paint a machine the wrong colour on the
 * sheet you open to decide whether a machine is reachable:
 *
 *   · THE BROWSER'S CLOCK. `Date.now()` against a server-stamped `last_seen`. A laptop
 *     whose clock is two minutes fast reads every machine as offline, and one that is
 *     slow reads dead machines as live. That is precisely what `serverNowS()` and the
 *     "null until learned" skew exist to prevent, and its own comment says so: nothing
 *     outside this module should be reasoning about freshness at all.
 *   · 120 SECONDS instead of 90.
 *   · NO STATE CHECK, so a machine mid-unpair rendered as live.
 *   · `live: null` COUNTED AS ONLINE if recent. Here it is `unknown` — and null is the
 *     normal case the moment there is more than one BFF instance, because "this
 *     instance does not hold the link" is not "the machine is gone".
 *
 * Exported so ServerPanel can ask instead of deciding. `ageS` is passed in rather than
 * computed here so the caller cannot accidentally supply a browser-clock age: the two
 * call sites both take it from `serverNowS()`.
 */
export function deviceLiveness(
  d: { state?: unknown; live?: unknown; last_seen?: unknown },
  ageS: number,
): PairedServer['reachable'] {
  if (d.state !== 'active') return 'offline'

  if ('live' in d) {
    if (d.live === true) return 'online'
    if (d.live === false) return 'offline'
    return 'unknown'
  }
  return ageS <= 90 && serverClockKnown() ? 'online' : 'unknown'
}

/** The server's now minus a device's `last_seen`, or Infinity when it has never been
 *  seen. Exported with the verdict above, since a caller needs both and must not build
 *  this one out of `Date.now()`. */
export function deviceAgeS(d: { last_seen?: unknown }): number {
  const lastSeen = typeof d.last_seen === 'number' ? d.last_seen : 0
  return lastSeen > 0 ? serverNowS() - lastSeen : Infinity
}

export function toServerList(devices: unknown[]): ServerList {
  const body = { devices }
  const servers = (body.devices as Record<string, unknown>[]).map((d): PairedServer => {
    const lastSeen = typeof d.last_seen === 'number' ? d.last_seen : 0
    const ageS = lastSeen > 0 ? serverNowS() - lastSeen : Infinity
    const hostname = typeof d.hostname === 'string' ? d.hostname : ''
    const nickname = typeof d.nickname === 'string' ? d.nickname.trim() : ''
    /*
     * The roster comes from a roster, never from `sessions`.
     *
     * avengents-server measured what that field actually holds on a real machine: 8
     * entries where 19 agents are attached, and the 8 are hex ids rather than names.
     * The missing 11 are agents on machines attached to this one — by design that
     * field answers only for the local host, so they are not late, they are never
     * coming. Rendering it as "the agents here" reports eleven running agents as
     * absent, and a short list is indistinguishable from a true one.
     *
     * Their rule, kept: a roster and a status must not share a field. Absent from a
     * roster means not attached; absent from a status means this machine has not
     * reported it, or does not own it. Merge them and the two can never be told apart
     * again — which is the same mistake as `null` versus `[]`, one level up.
     */
    /*
     * The roster, measured against the deployed payload rather than a description of
     * it: an array of objects, not of names. Mapping it with String() would have
     * painted a column of "[object Object]" — the same class of mistake as the hex
     * ids avengents-server caught, and it was in my parser because I read a message
     * instead of the response.
     */
    const roster = Array.isArray(d.agents)
      ? (d.agents as Record<string, unknown>[]).map((a) => ({
          channel_hash: String(a.channel_hash ?? ''),
          label: String(a.label ?? a.channel_hash ?? ''),
          session: typeof a.session === 'string' && a.session ? a.session : null,
          reported: a.reported === true,
          blocked: typeof a.blocked === 'string' ? a.blocked : null,
        }))
      : null
    return {
      last_seen_age_s: lastSeen > 0 ? Math.max(0, Math.round(serverNowS() - lastSeen)) : null,
      server_id: String(d.device_id ?? ''),
      /*
       * The account's nickname first (owner, 2026-08-19: Rename 給機器取暱稱,
       * 查表顯示) — this line is the ONE seam every display name flows through:
       * fetchServers and the account WS devices frames both come here, and
       * everywhere.ts stamps this name onto agent rows as device_name, which
       * machineNameOf prefers. Then hostname when the machine has reported one;
       * the UUID's first 8 otherwise — the SAME fallback homepage's /devices
       * table uses, so the two surfaces name a machine identically. An empty
       * hostname is deliberately not padded with a placeholder: "never reported
       * a name" usually travels with "never connected", and papering over one
       * hides the other.
       */
      name: deviceDisplayName({ nickname, hostname, device_id: String(d.device_id ?? '') }),
      fp: typeof d.device_fp === 'string' && d.device_fp ? d.device_fp.slice(0, 8) : null,
      version: typeof d.version === 'string' ? d.version : '',
      agents: roster,
      sibling_of: typeof d.sibling_of === 'string' && d.sibling_of ? d.sibling_of : null,
      /*
       * Derived from the beat, not asserted. The edge beats in on its own (≤30s per
       * the command-queue design), so:
       *   recent beat        → online
       *   no beat lately     → unknown — NOT offline. A missed beat is "go look",
       *                        an unpaired device is "expected". Same rule as every
       *                        presence dot in this codebase.
       *   pending_unpair     → offline: the user asked for it to be gone.
       */
      /*
       * Without an observed server clock, freshness is not claimable.
       *
       * `online` here would rest on subtracting two clocks that were never compared —
       * and the direction that hurts is a browser running behind, which paints a dead
       * machine as live. "unknown" is the honest word for that, and it is the same
       * rule this file already applies to a machine that has gone quiet: go and look.
       */
      /*
       * Observed liveness first; the clock only when nothing observed it.
       *
       * `live` is whether an instance actually holds that machine's connection —
       * three answers, not two. true: it is held here. false: it is not, and there is
       * nowhere else it could be. null: this instance does not hold it and a sibling
       * might, which is the NORMAL case once there is more than one. Drawing null as
       * offline would paint a connected machine as gone — the same wrong answer the
       * owner already met when a paired MacBook made the Linux box read as signed out.
       *
       * Key presence, not value: `live: null` is "nobody here knows", while no `live`
       * key at all is an older payload that never said. Only the second may fall back
       * to subtracting clocks, which is a guess this file spent the night removing.
       */
      reachable: deviceLiveness(d, ageS),
      agent_count: roster ? roster.length : null,
    }
  })
  /*
   * Live machines first, never-connected last. The owner's failure mode was picking
   * the dead twin from a list of identical UUIDs — ordering cannot fix the naming
   * (that needs a friendly name from the BFF), but it puts the machine that will
   * actually answer at the top.
   */
  servers.sort((a, b) => (a.last_seen_age_s ?? Infinity) - (b.last_seen_age_s ?? Infinity))
  /*
   * COLLIDING NAMES GET THE FINGERPRINT, centrally, so every surface inherits it.
   *
   * The owner restarted the older Mac's edge and its new build registered
   * hostname "Mac" — the same name as the newer MacBook. Two machines, one label,
   * in a picker whose whole job is telling them apart: the dead-twin problem this
   * file already sorts against, now with names instead of UUIDs. The fingerprint
   * is appended ONLY on collision — a unique name stays clean — and it is the same
   * hex the pairing screen shows, so a person can match machine to entry by
   * looking at the machine.
   */
  const seen = new Map<string, number>()
  for (const sv of servers) seen.set(sv.name, (seen.get(sv.name) ?? 0) + 1)
  for (const sv of servers) {
    if ((seen.get(sv.name) ?? 0) > 1 && sv.fp) sv.name = `${sv.name} (${sv.fp})`
  }
  return { kind: 'servers', servers }
}